import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, label } from "@/lib/api";
import { MATTER_STATUSES } from "@/lib/constants";
import { PageHeader, Pill, StatusDot, Empty, GoldButton } from "@/components/bits";
import { FormDialog } from "@/components/FormDialog";
import { matterFields } from "@/pages/ClientDetail";

export default function Matters() {
  const nav = useNavigate();
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [filter, setFilter] = useState("Open");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/matters").then((r) => setMatters(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/users").then((r) => setLawyers(r.data.filter((u) => u.role !== "support"))); }, []);
  const shown = matters.filter((m) => filter === "All" || m.status === filter);
  const verified = clients.filter((c) => c.kyc_status === "Verified");

  return (
    <div data-testid="matters-page">
      <PageHeader eyebrow="Matter management" title="Matters" subtitle="Status indicator: green on track · amber no activity in 14 days · red overdue court action"
        actions={<GoldButton onClick={() => setOpen(true)} data-testid="new-matter-button"><Plus className="w-4 h-4 mr-2" /> New matter</GoldButton>} />
      <div className="flex gap-2 mb-4">{["Open", "On Hold", "Closed", "All"].map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`matters-filter-${s.toLowerCase().replace(" ", "-")}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "border-[var(--brand)] text-[var(--brand-hover)] bg-[color-mix(in_srgb,var(--brand)_10%,transparent)]" : "border-[#2E3B61] text-slate-400 hover:text-white"}`}>{s}</button>)}</div>
      <div className="panel overflow-hidden fade-in">
        <table className="w-full">
          <thead><tr><th className="th w-8"></th><th className="th">Matter</th><th className="th">Client</th><th className="th">Type</th><th className="th">Lawyer</th><th className="th">Opened</th><th className="th">Last activity</th><th className="th">Status</th></tr></thead>
          <tbody>{shown.map((m) => (
            <tr key={m.id} className="row-link" onClick={() => nav(`/matters/${m.id}`)} data-testid={`matter-row-${m.matter_number}`}>
              <td className="td"><StatusDot color={m.indicator} /></td>
              <td className="td"><span className="font-mono gold-text">{m.matter_number}</span><p className="text-xs text-slate-400 truncate max-w-md">{m.description}</p></td>
              <td className="td text-white">{m.client_name}</td><td className="td text-slate-300">{label(m.matter_type)}</td>
              <td className="td text-slate-300">{m.lawyer_name}</td><td className="td text-slate-400">{fmtDate(m.date_opened)}</td>
              <td className="td text-slate-400">{m.days_since_activity}d ago</td><td className="td"><Pill value={m.status} /></td>
            </tr>))}</tbody>
        </table>
        {shown.length === 0 && <Empty text="No matters in this view." testId="matters-empty" />}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title="Open new matter" description="Only clients with Verified KYC can be selected."
        fields={[{ name: "client_id", label: "Client (KYC verified)", type: "select", required: true, col: 2, options: verified.map((c) => ({ value: c.id, label: c.full_name })) }, ...matterFields(lawyers)]}
        initial={{ matter_type: "other", responsible_lawyer_id: lawyers[0]?.id, client_id: verified[0]?.id }} testPrefix="matter"
        onSubmit={async (v) => { const r = await api.post("/matters", v); toast.success(`Matter ${r.data.matter_number} opened`); nav(`/matters/${r.data.id}`); }} />
    </div>
  );
}
